<template>
  <v-navigation-drawer
    id="component-list-panel"
    v-model="miscDrawer"
    :clipped="true"
    :temporary="true"
    right
    app
    :width="componentListWidth"
    :class="{ 'is-resizing': isResizing }"
    overlay-color="var(--ds-color-reverse)"
  >
    <div
      ref="resizeHandle"
      class="component-list-resize-handle"
      role="separator"
      aria-controls="component-list-panel"
      aria-orientation="vertical"
      :aria-label="$t('components.ChooseCardMenu.resizePanel')"
      :aria-valuemin="minPanelWidth"
      :aria-valuemax="maxPanelWidth"
      :aria-valuenow="componentListWidth"
      tabindex="0"
      @pointerdown.prevent="startResize"
      @lostpointercapture="stopResize"
      @keydown.left.prevent="resizeByKeyboard(1)"
      @keydown.right.prevent="resizeByKeyboard(-1)"
    />
    <div class="component-list-header pa-5 pb-1">
      <h3>
        {{ $t('components.ChooseCardMenu.listTitle') }}
      </h3>
      <v-btn
        v-if="panelRestoreWidth === null"
        v-tooltip="$t('components.ChooseCardMenu.maximizePanel')"
        icon
        :aria-label="$t('components.ChooseCardMenu.maximizePanel')"
        @click="maximizePanel"
      >
        <v-icon>mdi-arrow-expand-horizontal</v-icon>
      </v-btn>
      <v-btn
        v-else
        v-tooltip="$t('components.ChooseCardMenu.minimizePanel')"
        icon
        :aria-label="$t('components.ChooseCardMenu.minimizePanel')"
        @click="minimizePanel"
      >
        <v-icon>mdi-arrow-collapse-horizontal</v-icon>
      </v-btn>
    </div>
    <v-list
      dense
    >
      <draggable
        v-model="cards"
        @end="onDragEnd"
        @start="onStart"
      >
        <v-list-item
          v-for="card in cards"
          :key="card.id"
          class="draggable"
          :disabled="shouldDisableChanges"
        >
          <v-list-item-action>
            <v-row>
              <StIcon
                :disabled="shouldDisableChanges"
                icon-name="drag"
              />
              <v-checkbox
                v-model="card.visible"
                v-ripple
                :disabled="shouldDisableChanges"
                @change="saveCardVisibility(card.visible)"
              />
            </v-row>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title
              ref="cardTitles"
              v-tooltip="isTitleOverflowing(card.id) ? (resolveTitle(card) || $t('components.AnnotationTextCard.insert')) : ''"
              ref-in-for
              :data-card-id="card.id"
            >
              {{ resolveTitle(card) ? resolveTitle(card) : $t('components.AnnotationTextCard.insert') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </draggable>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { MUTATE } from '@/store/modules/mutation_types';
import draggable from 'vuedraggable';
import StIcon from '@stilingue/st-icon';
import cardsModel from '@/mixins/cards-model';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import { resolveCardTitle } from '@/utils/card-title-resolver';

const DEFAULT_PANEL_WIDTH = 300;
const MIN_PANEL_WIDTH = 280;
const MAX_PANEL_WIDTH = 720;
const MAX_VIEWPORT_WIDTH_RATIO = 0.5;
const KEYBOARD_RESIZE_STEP = 32;
const haveSameCardIds = (cardIds, otherCardIds) => cardIds.length === otherCardIds.length &&
  cardIds.every((cardId, index) => cardId === otherCardIds[index]);

export default {
  name: 'ChooseCardMenu',
  components: {
    draggable,
    StIcon,
  },
  mixins: [
    cardsModel,
  ],
  data() {
    return {
      grabClassName: 'grabbing',
      componentListWidth: Math.max(
        1,
        Math.min(
          DEFAULT_PANEL_WIDTH,
          MAX_PANEL_WIDTH,
          Math.floor(window.innerWidth * MAX_VIEWPORT_WIDTH_RATIO),
        ),
      ),
      viewportWidth: window.innerWidth,
      isResizing: false,
      resizeStartX: 0,
      resizeStartWidth: 0,
      resizePointerId: null,
      panelRestoreWidth: null,
      hasManuallyResizedPanel: false,
      overflowingCardIds: [],
    };
  },
  computed: {
    maxPanelWidth() {
      return Math.max(1, Math.min(MAX_PANEL_WIDTH, Math.floor(this.viewportWidth * MAX_VIEWPORT_WIDTH_RATIO)));
    },
    minPanelWidth() {
      return Math.min(MIN_PANEL_WIDTH, this.maxPanelWidth);
    },
    ...mapState('misc', [
      'drawer',
    ]),
    miscDrawer: {
      get() {
        return this.drawer;
      },
      set(newValue) {
        this.mutateMisc({
          property: 'drawer',
          with: newValue,
        });
      },
    },
    htmlTag() {
      return document.getElementsByTagName('html').item(0);
    },
  },
  watch: {
    drawer() {
      const html = document.getElementsByTagName('html')[0];
      if (this.drawer) {
        html.classList.add('overflow-hidden');
        return true;
      }
      this.stopResize();
      html.classList.remove('overflow-hidden');
    },
  },
  mounted() {
    window.addEventListener('resize', this.onViewportResize);
    this.onViewportResize();
  },
  updated() {
    this.updateOverflowingTitles();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onViewportResize);
    this.stopResize();
  },
  methods: {
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    onDragEnd() {
      this.htmlTag.classList.remove(this.grabClassName);

      trackEventWithUserProperties('ST CardList Card Edit');
    },
    onStart() {
      this.htmlTag.classList.add(this.grabClassName);
    },
    startResize(event) {
      if (this.isResizing) return;

      this.isResizing = true;
      this.resizePointerId = event.pointerId;
      this.resizeStartX = event.clientX;
      this.resizeStartWidth = this.componentListWidth;
      event.currentTarget.setPointerCapture(event.pointerId);
      window.addEventListener('pointermove', this.resizePanel);
      window.addEventListener('pointerup', this.stopResize);
      window.addEventListener('pointercancel', this.stopResize);
    },
    resizePanel(event) {
      if (!this.isResizing || event.pointerId !== this.resizePointerId) return;

      const requestedWidth = this.resizeStartWidth + this.resizeStartX - event.clientX;
      const width = this.clampPanelWidth(requestedWidth);
      if (width !== this.componentListWidth) {
        this.hasManuallyResizedPanel = true;
        this.componentListWidth = width;
      }
    },
    resizeByKeyboard(direction) {
      const requestedWidth = this.componentListWidth + (direction * KEYBOARD_RESIZE_STEP);
      const width = this.clampPanelWidth(requestedWidth);
      if (width !== this.componentListWidth) {
        this.hasManuallyResizedPanel = true;
        this.componentListWidth = width;
      }
    },
    clampPanelWidth(width) {
      return Math.max(this.minPanelWidth, Math.min(this.maxPanelWidth, width));
    },
    onViewportResize() {
      this.viewportWidth = window.innerWidth;
      this.componentListWidth = this.clampPanelWidth(this.componentListWidth);
      this.updateOverflowingTitles();
    },
    stopResize(event) {
      if (!this.isResizing || (event && event.pointerId !== this.resizePointerId)) return;

      const pointerId = this.resizePointerId;
      this.resizePointerId = null;
      this.isResizing = false;
      window.removeEventListener('pointermove', this.resizePanel);
      window.removeEventListener('pointerup', this.stopResize);
      window.removeEventListener('pointercancel', this.stopResize);

      const resizeHandle = this.$refs.resizeHandle;
      if (resizeHandle.hasPointerCapture(pointerId)) {
        resizeHandle.releasePointerCapture(pointerId);
      }
    },
    maximizePanel() {
      if (this.panelRestoreWidth === null) {
        this.panelRestoreWidth = this.componentListWidth;
      }
      this.hasManuallyResizedPanel = true;
      this.componentListWidth = this.maxPanelWidth;
    },
    minimizePanel() {
      if (this.panelRestoreWidth === null) {
        return;
      }
      this.hasManuallyResizedPanel = true;
      this.componentListWidth = this.clampPanelWidth(this.panelRestoreWidth);
      this.panelRestoreWidth = null;
    },
    isTitleOverflowing(cardId) {
      return this.overflowingCardIds.includes(String(cardId));
    },
    updateOverflowingTitles() {
      const titleRefs = this.$refs.cardTitles || [];
      const titleComponents = Array.isArray(titleRefs) ? titleRefs : [titleRefs];
      let widestOverflow = 0;
      const overflowingCardIds = titleComponents.reduce((ids, titleComponent) => {
        const titleElement = titleComponent.$el || titleComponent;
        const overflow = titleElement.scrollWidth - titleElement.clientWidth;
        if (overflow > 0) {
          ids.push(titleElement.getAttribute('data-card-id'));
          widestOverflow = Math.max(widestOverflow, overflow);
        }
        return ids;
      }, []);
      if (widestOverflow > 0 && !this.hasManuallyResizedPanel) {
        this.componentListWidth = this.clampPanelWidth(this.componentListWidth + widestOverflow);
      }
      const hasSameOverflowingCards = haveSameCardIds(overflowingCardIds, this.overflowingCardIds);

      if (!hasSameOverflowingCards) {
        this.overflowingCardIds = overflowingCardIds;
      }
    },
    saveCardVisibility(visibility) {
      this.saveCards();

      trackEventWithUserProperties('ST CardList Card On/Off', { On: visibility });
    },
    resolveTitle(card) {
      const title = resolveCardTitle(card, this);
      const { originalTitle, titleCustomized } = card.props;

      return titleCustomized && title && originalTitle && title !== originalTitle
        ? `${title} (${originalTitle})`
        : title;
    },
  },
};
</script>

<style scoped lang="scss">
  .theme--dark h3 {
    color: white;
  }

  .draggable {
    cursor: grab;
  }

  .component-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
      margin: 0;
    }
  }

  .component-list-resize-handle {
    position: absolute;
    z-index: 1;
    top: 0;
    bottom: 0;
    left: 0;
    width: 8px;
    cursor: col-resize;

    &:hover,
    &:focus-visible {
      background-color: var(--ds-color-overlay);
    }
  }

  .v-navigation-drawer--temporary.is-resizing {
    transition: none;
    user-select: none;
  }

  [draggable="true"] {
    opacity: 0.5;
    background: gray;
  }

  .v-navigation-drawer--temporary {
    z-index: 999 !important;
  }
</style>
